import { Accordion, Button, Heading, Modal, style, Tag, TriangleExclamationIcon, UpdateIcon } from "@r360/library";
import * as Sentry from "@sentry/react";
import React, { useEffect, useState } from "react";
import { FallbackProps, ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";
import { renderInScope } from "../..";
import { getWindowR360 } from "../../Helper";
import { isSentryInitialized } from "../../Sentry";

type TErrorFallbackModal = { error: FallbackProps; sentryErrorId: string | undefined; onClose?: () => void };

export const ErrorFallbackModal = ({ error, sentryErrorId, onClose }: TErrorFallbackModal) => {
    const [showModal, setShowModal] = useState(true);

    useEffect(() => {
        if (sentryErrorId) {
            console.log("sentryErrorId", sentryErrorId);
        }
    }, [sentryErrorId]);

    // useEffect(() => {
    //     const timer = setTimeout(() => {
    //         setShowModal(true);
    //     }, 300);

    //     return () => clearTimeout(timer);
    // }, []);

    const handleCloseModal = () => {
        setShowModal(false);
        onClose?.();
    };

    const texts = getWindowR360().__TEXTS__;
    const baseName = getWindowR360().__OVERRIDE_BASENAME__ || process.env.PUBLIC_URL || "/";

    return (
        <Modal open={showModal} onClose={handleCloseModal} closeOnOutsideClick={false}>
            <div className="u-d-flex u-gap-18 u-align-items-start u-mb-30">
                <div style={{ flexShrink: 0 }}>
                    <TriangleExclamationIcon size={36} color={style.alertDarkColor} />
                </div>
                <div>
                    <Heading type="h2" styleAs="h3">
                        {texts?.["error-heading"] ?? "Oops! Something went wrong."}
                    </Heading>
                    <p>{texts?.["error-description"] ?? "Please go back to the homepage or try again."}</p>
                    <Accordion stripped title={texts?.["show-more"] ?? "Show more"}>
                        <p className="u-mt-6 u-mb-24">
                            <i>{`"${error.error.message}"`}</i>
                        </p>
                        {sentryErrorId && (
                            <p className="u-mb-24">
                                <span className="u-fw-medium">Felkod: </span>
                                <Tag theme="light-blue" text={sentryErrorId} />
                            </p>
                        )}
                    </Accordion>
                </div>
            </div>
            <div className="u-d-flex u-gap-12 u-justify-content-end">
                <Button type="secondary" leftIcon={<UpdateIcon />} onClick={() => window.location.reload()}>
                    {texts?.["reload"] ?? "Reload page"}
                </Button>
                <Button link linkTo={`${baseName}`} reactRouterLink={false}>
                    {texts?.["to-startpage"] ?? "Go to startpage"}
                </Button>
            </div>
        </Modal>
    );
};

type TErrorBoundary = { children: React.ReactNode; outsideScope?: boolean };

export const ErrorBoundary = ({ children, outsideScope = false }: TErrorBoundary) => {
    const renderFallback = (error: FallbackProps) => {
        let sentryId: string | undefined = undefined;

        if (isSentryInitialized()) {
            sentryId = Sentry.captureException(error.error);
        }

        if (outsideScope) {
            return renderInScope(
                <ErrorFallbackModal error={error} sentryErrorId={sentryId} onClose={() => error.resetErrorBoundary()} />
            );
        }

        return <ErrorFallbackModal error={error} sentryErrorId={sentryId} onClose={() => error.resetErrorBoundary()} />;
    };

    return <ReactErrorBoundary FallbackComponent={renderFallback}>{children}</ReactErrorBoundary>;
};
